import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setCoupon } from '../slices/couponSlice'
import { setOffer } from '../slices/offerSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const offersApi = createApi({
  reducerPath: 'offersApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'AllOffers',
    'OfferDetails',
    'OfferIncludedGroups',
    'OfferExcludedGroups',
    'OfferCoupons',
    'OfferAvailableCoupons',
    'OffersFromCategory',
    'OfferCouponStatus',
    'LiveFundsOfferCount'
  ],
  endpoints: (builder) => ({
    getOffers: builder.query({
      query: (requestParams: GetOffersParam) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'searchCoupons',
          requestParams
        ),
      providesTags: ['AllOffers']
    }),
    getAllCouponCategoryList: builder.query({
      query: (requestParams: GetCategoryListParam) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'getAllCategoryList',
          requestParams
        )
    }),
    getOfferDetailsByID: builder.query({
      query: (requestParams: GetOfferDetailsParam) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'retrieveOfferById',
          requestParams
        ),
      providesTags: ['OfferDetails'],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: GetOfferDetailsResponse
          }
          if (data?.result) {
            dispatch(setOffer(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    getCouponsStatusByOfferID: builder.query({
      query: (requestParams: GetOfferDetailsParam) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'getCouponsByOfferID',
          requestParams
        ),
      providesTags: ['OfferCouponStatus'],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: GetOfferCouponsResponse
          }
          if (data?.result) {
            dispatch(setCoupon(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    getRepeatableCouponsByOfferId: builder.query({
      query: (requestparams: CommonOfferParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'getRepeatableCoupons',
          requestparams
        )
    }),
    updateOfferStatus: builder.mutation({
      query: (requestParams: UpdateOfferStatusParam) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'updateOfferStatus',
          requestParams
        ),
      invalidatesTags: ['OfferDetails', 'LiveFundsOfferCount', 'AllOffers']
    }),
    getOfferIncludedTargetGroups: builder.query({
      query: (requestParams: CommonOfferParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getOfferTargetGroups',
          requestParams
        ),
      providesTags: [{ type: 'OfferIncludedGroups' }]
    }),
    getOfferExcludedTargetGroups: builder.query({
      query: (requestParams: CommonOfferParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getExcludedOfferTargetGroups',
          requestParams
        ),
      providesTags: [{ type: 'OfferExcludedGroups' }]
    }),
    removeOfferFromGroup: builder.mutation({
      query: (requestParams: CommonOfferTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'removeOfferFromGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'OfferIncludedGroups' },
        { type: 'OfferExcludedGroups' },
        { type: 'AllOffers' }
      ]
    }),
    addOfferToIncludedGroup: builder.mutation({
      query: (requestParams: CommonOfferTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'addOfferToGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'OfferIncludedGroups' },
        { type: 'OfferExcludedGroups' },
        { type: 'AllOffers' }
      ]
    }),
    addOfferToExcludedGroup: builder.mutation({
      query: (requestParams: CommonOfferTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'excludeOfferFromGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'OfferIncludedGroups' },
        { type: 'OfferExcludedGroups' },
        { type: 'AllOffers' }
      ]
    }),
    getOfferCoupons: builder.query({
      query: (requestParams: SearchOfferCouponsParams) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'searchOfferCoupons',
          requestParams
        ),
      providesTags: [{ type: 'OfferCoupons' }]
    }),
    deleteAvailableCoupons: builder.mutation({
      query: (requestParams: DeleteOfferCouponsParams) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'deleteAvailableCoupons',
          requestParams
        ),
      invalidatesTags: [
        { type: 'OfferAvailableCoupons' },
        { type: 'OfferCoupons' },
        { type: 'OfferCouponStatus' },
        { type: 'AllOffers' }
      ]
    }),
    refundOfferCoupon: builder.mutation({
      query: (requestParams: { CouponID: number }) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'processRefundForCoupon',
          requestParams
        ),
      invalidatesTags: [
        { type: 'OfferAvailableCoupons' },
        { type: 'OfferCoupons' }
      ]
    }),
    getAllAvailableCoupons: builder.query({
      query: (requestParams: { OfferID: number }) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'downloadAvailableCoupons',
          requestParams
        ),
      providesTags: [{ type: 'OfferAvailableCoupons' }]
    }),
    getOffersInfoFromCategory: builder.query({
      query: (requestParams: GetOffersCategoryInfoParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'getOffersInfoFromCategory',
          requestParams
        ),
      providesTags: [{ type: 'OffersFromCategory' }]
    }),
    updateOfferUiPositions: builder.mutation({
      query: (requestParams: OfferUpdateUiParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'updateUiPositions',
          requestParams
        ),
      invalidatesTags: ['OffersFromCategory']
    }),
    validateOfferSteps: builder.mutation({
      query: (requestParams: OfferValidationParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'validateOfferSteps',
          requestParams
        )
    }),
    getAllProvidersForOffer: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'getAllProvidersForOffer',
          requestParams
        )
    }),
    getAllClientsForOffer: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'getAllApiList',
          requestParams
        )
    }),
    createOffer: builder.mutation({
      query: (requestParams: OfferParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'createOffer',
          requestParams
        ),
      invalidatesTags: [
        'OffersFromCategory',
        'LiveFundsOfferCount',
        'AllOffers'
      ]
    }),
    updateOffer: builder.mutation({
      query: (requestParams: OfferParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'updateOffer',
          requestParams
        ),
      invalidatesTags: [
        'OffersFromCategory',
        'OfferDetails',
        'LiveFundsOfferCount',
        'AllOffers'
      ]
    }),
    validateOfferRepeatableCoupons: builder.query({
      query: (requestParams: OfferRepeatableCouponsParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'validateOfferRepeatableCoupons',
          requestParams
        )
    }),
    insertOfferRepeatableCoupons: builder.mutation({
      query: (requestParams: OfferRepeatableCouponsParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'insertOfferRepeatableCoupons',
          requestParams
        ),
      invalidatesTags: [
        'OfferCouponStatus',
        'OfferDetails',
        'OfferCoupons',
        'OfferAvailableCoupons',
        'AllOffers'
      ]
    }),
    processOfferUniqueCoupons: builder.query({
      query: (requestParams: ProcessOfferUniqueCouponsParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'processOfferUniqueCoupons',
          requestParams
        )
    }),
    insertOfferUniqueCoupons: builder.mutation({
      query: (requestParams: OfferUniqueCouponsParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'insertOfferUniqueCoupons',
          requestParams
        ),
      invalidatesTags: [
        'OfferCouponStatus',
        'OfferDetails',
        'OfferCoupons',
        'OfferAvailableCoupons',
        'AllOffers'
      ]
    }),
    getAllActiveOffers: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'getAllActiveOffers',
          requestParams
        )
    }),
    fetchOffersForPeriod: builder.mutation({
      query: (requestParams: FetchDataWithPeriodParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'fetchOffersForPeriod',
          requestParams
        )
    }),
    downloadAvailableCouponsFromOffer: builder.mutation({
      query: (requestParams: DownloadCouponsFromOfferParams) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'downloadAvailableCouponsFromOffer',
          requestParams
        )
    }),
    getMSBProducts: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'fetchMSBProducts',
          requestParams
        )
    }),
    getAllActiveOffersForReferral: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'getAllActiveOffersForReferral',
          requestParams
        )
    }),
    getLiveFundsOfferCount: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.COUPONS_RPC_PATH,
          'getLiveFundsOfferCount',
          requestParams
        ),
      providesTags: ['LiveFundsOfferCount']
    }),
    disableOffers: builder.mutation({
      query: (requestParams: DisableOffersParams) =>
        generateRPCQuery(
          apiConstants.OFFERS_RPC_PATH,
          'disableOffers',
          requestParams
        ),
      invalidatesTags: ['AllOffers']
    })
  })
})

export const {
  useUpdateOfferStatusMutation,
  useRemoveOfferFromGroupMutation,
  useAddOfferToExcludedGroupMutation,
  useAddOfferToIncludedGroupMutation,
  useDeleteAvailableCouponsMutation,
  useRefundOfferCouponMutation,
  useGetAllAvailableCouponsQuery,
  useGetAllCouponCategoryListQuery,
  useGetCouponsStatusByOfferIDQuery,
  useGetOfferCouponsQuery,
  useGetOfferDetailsByIDQuery,
  useLazyGetOfferDetailsByIDQuery,
  useGetOfferExcludedTargetGroupsQuery,
  useGetOfferIncludedTargetGroupsQuery,
  useGetOffersQuery,
  useGetRepeatableCouponsByOfferIdQuery,
  useUpdateOfferUiPositionsMutation,
  useValidateOfferStepsMutation,
  useGetAllProvidersForOfferQuery,
  useGetAllClientsForOfferQuery,
  useCreateOfferMutation,
  useGetOffersInfoFromCategoryQuery,
  useLazyGetOffersInfoFromCategoryQuery,
  useUpdateOfferMutation,
  useLazyValidateOfferRepeatableCouponsQuery,
  useInsertOfferRepeatableCouponsMutation,
  useLazyProcessOfferUniqueCouponsQuery,
  useInsertOfferUniqueCouponsMutation,
  useGetAllActiveOffersQuery,
  useFetchOffersForPeriodMutation,
  useDownloadAvailableCouponsFromOfferMutation,
  useLazyGetMSBProductsQuery,
  useGetAllActiveOffersForReferralQuery,
  useGetLiveFundsOfferCountQuery,
  useDisableOffersMutation
} = offersApi
